import * as React from "react";
import * as moment from "moment";
import {
    IFalconConvenienceApiService,
    Policy,
    POLICY_STATUS_MTC,
    POLICY_STATUS_PENDING_RENEWAL,
    Renewal,
    RENEWAL_PROPOSAL_EDIT_VIEW_JOURNEY_URL,
    resourcesHelper,
    Quote,
    Subscription,
    Types
} from "@falcon/services";
import {ImgTileLink} from "../../../../../components/ImgTileLink";
import {Link} from "react-router-dom";
import { PRODUCT_TILE_COPY } from "../../../common/productTileCopy";
import {getSavedValidQuotesCount, isAllianzAdvantagesToggleEnabled} from "../../../../../CommonFunctions";
import {lazyInject} from "@athena-portal/core";
import * as Driver from "driver.js";

const { FAQ_CAR_URL } = resourcesHelper;

const isSettingEnabled = (value: string | boolean | undefined): boolean => value === true || value === "true";

const buildDigitalClaimsUrl = (baseUrl: string | undefined, policyNumber: string): string | undefined => {
    if (!baseUrl) {
        return undefined;
    }

    const separator = baseUrl.includes("?") ? "&" : "?";

    return `${baseUrl}${separator}journey=digital&policy=${encodeURIComponent(policyNumber)}`;
};

const renderQuoteBadge = (quoteCount: number, isMobile: boolean): React.ReactNode => {
    const quoteText: string = quoteCount === 1 ? "quote" : "quotes";
    if (isMobile) {
        return (<p><span className="muli-bold-16">{quoteCount}</span> {quoteText}</p>);
    }
    return (
        <p>You have <span className="muli-bold-16">{quoteCount}</span> {quoteText}</p>
    );
};

interface IAnnualTilesProps {
    policies?: Policy[];
    subscription?: Subscription;
    renewalProposal?: Renewal;
    policyNumber: string;
    hasExpiredPolicies?: boolean;
    hasOldPolicies?: boolean;
    hasMultiplePolicies?: boolean;
    renewalEnabled: boolean;
    hasOldHomePolicies?: boolean;
    quotes?: Quote[];
    tourDriver?: Driver
}

interface IState {
    allianzDirectClaimsEnabled: boolean;
}

interface ISettings {
    allianzDirectClaimsEnabled: string | boolean;
}

function prepareFlags(policies: Policy[] | undefined, renewalProposal: Renewal | undefined) {
    const hasPolicies = policies && policies.length > 0;
    const hasPendingRenewal: boolean = renewalProposal != null && renewalProposal.status === POLICY_STATUS_PENDING_RENEWAL;
    const policy: Policy | null = policies && policies.length > 0 ? policies[0] : null;
    const hasActivePolicy: boolean = policy != null && moment.utc(policy.endDate, moment.ISO_8601).isSameOrAfter(moment(), 'day');
    const policyCanceled = policy != null && policy.status === POLICY_STATUS_MTC;
    const renewalStatus: object = {
        "status": hasPendingRenewal ? "Existing" : undefined
    };
    const inGracePeriod: boolean = policy?.annualPolicyInfo ? policy.annualPolicyInfo.inGracePeriod : false;
    const futureCancelledPolicy : boolean = policy != null && moment.utc(policy.endDate, moment.ISO_8601).isAfter(moment());
    const futureCancelledPolicyBeforeInception: boolean = policy != null && moment.utc(policy.endDate, moment.ISO_8601).isSame(moment.utc(policy.startDate, moment.ISO_8601))
        && moment.utc(policy.startDate, moment.ISO_8601).isAfter(moment(), 'day');
    return {
        hasPolicies,
        hasPendingRenewal,
        policy,
        hasActivePolicy,
        policyCanceled,
        renewalStatus,
        inGracePeriod,
        futureCancelledPolicy,
        futureCancelledPolicyBeforeInception
    };
}

export class AnnualTiles extends React.Component<IAnnualTilesProps, IState> {

    public readonly state: IState = {
        allianzDirectClaimsEnabled: false
    };

    @lazyInject(Types.FalconConvenienceApiService)
    private readonly falconConvenienceApiService: IFalconConvenienceApiService;

    public componentDidMount(): void {
        this.falconConvenienceApiService
            .getSettings()
            .subscribe((settings: ISettings): void => {
                this.setState({
                    allianzDirectClaimsEnabled: isSettingEnabled(settings?.allianzDirectClaimsEnabled)
                });
            });
    }

    public render(): React.ReactNode {
        const props = this.props;

        const {
            policies,
            hasExpiredPolicies,
            hasOldPolicies,
            subscription,
            renewalProposal,
            policyNumber,
            hasMultiplePolicies,
            hasOldHomePolicies
        } = props;

        const {
            hasPolicies,
            hasPendingRenewal,
            policy,

            hasActivePolicy,
            policyCanceled,
            renewalStatus,
            inGracePeriod,
            futureCancelledPolicy,
            futureCancelledPolicyBeforeInception
        } = prepareFlags(policies, renewalProposal);

        const BRAND_NAME = resourcesHelper.THEME;
        const productIconText = PRODUCT_TILE_COPY[BRAND_NAME] ?? PRODUCT_TILE_COPY.Default;
        const isBritannia = resourcesHelper.THEME === "Britannia";
        const quoteBadgeText = renderQuoteBadge(getSavedValidQuotesCount(props.quotes), false);
        const quoteMobileBadgeText = renderQuoteBadge(getSavedValidQuotesCount(props.quotes), true);
        const adminFee = policy?.interaction?.paymentDetails?.instalmentPlan?.instalmentPlanParameters?.adminFee;

        const defaultMotorClaimsLink = `/dashboard/pickAnnualMotorClaimKind/${policyNumber}`;

        const motorDirectClaimsLink = buildDigitalClaimsUrl(
            process.env.REACT_APP_MOTOR_OTHER_CLAIM_URL,
            policyNumber
        );

        const useMotorDirectClaimsLink = !isBritannia && this.state.allianzDirectClaimsEnabled && motorDirectClaimsLink != null;

        const motorClaimsLink = useMotorDirectClaimsLink && motorDirectClaimsLink
            ? motorDirectClaimsLink
            : defaultMotorClaimsLink;

        return (
            <div className="link-grid">
                <div className="d-flex flex-wrap">

                    {hasPendingRenewal && (
                        <div className="flexItem pending-renewal">
                            <ImgTileLink
                                src={inGracePeriod ? `/${resourcesHelper.THEME}/img/pending_renewal_gray.svg` : `/${resourcesHelper.THEME}/img/pending_renewal.png`}
                                alt="Policy renewal icon"
                                to={RENEWAL_PROPOSAL_EDIT_VIEW_JOURNEY_URL + renewalProposal!._id}
                                status="New"
                                hasInfoButton={true}
                                tourDriver={this.props.tourDriver}
                                linkDisabled={inGracePeriod}
                                backButtonName={inGracePeriod ? "Cancel" : "Back"}
                                infoModalButtonHidden={inGracePeriod}
                                infoModalTitle="Your renewal, make changes"
                                infoModalAdditionalDescription={inGracePeriod ?
                                    <div className="banner-text banner-warn-renewal">
                                        <div className="banner-text">
                                            <p className="font-weight-bold">You are no longer covered</p>
                                            <p>
                                                You must renew your {resourcesHelper.PRODUCT} Car Insurance policy
                                                before making any changes to your cover
                                            </p>
                                            <Link className="color-cyan-blue"
                                                  to={RENEWAL_PROPOSAL_EDIT_VIEW_JOURNEY_URL + renewalProposal!._id}>
                                                <p className="font-weight-bold hyperlink mt-2 mb-2">
                                                    <img alt="Arrow icon"
                                                         src={`/${resourcesHelper.THEME}/img/arrow-right.svg`}/>Renew
                                                    your policy now
                                                </p>
                                            </Link>

                                        </div>
                                    </div> : ""}
                                infoModalBulletPoints={["Make changes to your policy for next year (e.g. add a driver).",
                                    "Choose to pay in one go or monthly (if available).",
                                    "Add or remove optional extras for next year (e.g. breakdown cover).",
                                    "Review your level of cover and compare options (if eligible)."]}
                                {...props}>
                                Your renewal, make changes
                            </ImgTileLink>
                        </div>
                    )}

                    {hasActivePolicy && !policyCanceled && !futureCancelledPolicyBeforeInception && (
                        <div className="flexItem policy-summary">
                            <ImgTileLink src={`/${resourcesHelper.THEME}/img/policy-summary.png`}
                                         alt="Policy summary icon"
                                         to={`/dashboard/annualMotor/policySummary/${policyNumber}`}
                                         hasInfoButton={true}
                                         tourDriver={this.props.tourDriver}
                                         infoModalTitle={`Your ${resourcesHelper.PRODUCT} Car Insurance policy`}
                                         infoModalBulletPoints={[
                                             `View a short breakdown of your ${resourcesHelper.PRODUCT} Car Insurance policy`
                                         ]}
                                         infoModalButton="Policy summary"
                                         {...renewalStatus}
                                         {...props}>
                                {resourcesHelper.THEME === "Allianz" ? "Policy summary" : "Your car policy"}
                            </ImgTileLink>
                        </div>
                    )}

                    {policyCanceled && !futureCancelledPolicy && (<div className="flexItem old-policies">
                            <ImgTileLink src={`/${resourcesHelper.THEME}/img/old-policies.png`}
                                         alt="Policy summary"
                                         to={`/dashboard/annualMotorOldPolicies/${policyNumber}`}
                                         hasInfoButton={true}
                                         tourDriver={this.props.tourDriver}
                                         infoModalTitle="Policy Summary"
                                         infoModalBulletPoints={[`View a short breakdown of your ${resourcesHelper.PRODUCT} Car Insurance policy`]}
                                         {...props}>
                                Policy summary
                            </ImgTileLink>
                        </div>
                    )}


                    {!inGracePeriod && hasPolicies && !policyCanceled && (
                        <div className="flexItem car">
                            <ImgTileLink id="carTile"
                                         src={`/${resourcesHelper.THEME}/img/wolf-your-car.png`}
                                         alt="Car icon"
                                         to={`/dashboard/annualMotor/yourCars/${policyNumber}`}
                                         hasInfoButton={true}
                                         tourDriver={this.props.tourDriver}
                                         infoModalTitle="Your car, make changes"
                                         infoModalBulletPoints={[
                                             "Change your car or edit the details", "Edit driver details",
                                             "Add/remove additional drivers", "Make changes to your policy (e.g. Business user, mileage etc.)",
                                             "Add or remove optional extras (e.g. Breakdown cover)"
                                         ]}
                                         infoModalAdditionalDescription={
                                             <h4 className="mb-3 font-weight-400">
                                                 {isBritannia ? (
                                                     <>
                                                         With {resourcesHelper.PRODUCT} Car Insurance, you're in
                                                         control. Changing your vehicle,
                                                         adding and removing drivers will incur
                                                         a <strong>&pound;{adminFee} admin fee</strong>. Remember,
                                                         though, any changes you make can still change your price.
                                                     </>
                                                 ) : (
                                                     <>
                                                         With {resourcesHelper.PRODUCT}, you're in control. Because you
                                                         make changes to your
                                                         policy yourself, there are <strong>no admin fees</strong>!
                                                         Remember, though, any
                                                         changes you make can still change your price.
                                                     </>
                                                 )}
                                             </h4>}
                                         {...renewalStatus}
                                         {...props}>
                                Your car, make changes
                            </ImgTileLink>
                        </div>
                    )}

                    {hasPolicies && <div className="flexItem payment">
                        <ImgTileLink id="paymentTile"
                                     src={`/${resourcesHelper.THEME}/img/payment-card-icon.png`}
                                     alt="Payment icon"
                                     to={`/dashboard/annualPaymentHistory/${policyNumber}`}
                                     notification={hasPolicies && !policyCanceled && subscription?.nextPaymentAmount != null}
                                     hasInfoButton={true}
                                     tourDriver={this.props.tourDriver}
                                     infoModalTitle="Payment"
                                     infoModalBulletPoints={["Change your payment day", "Edit your monthly payment details",
                                         "Change your payment card", "Pay off your balance",
                                         "View payment history"]}
                                     {...props}>
                            Payment
                        </ImgTileLink>
                    </div>}

                {hasPolicies && <div className="flexItem documents">
                    <ImgTileLink id="policyDocumentsTile"
                                 src={`/${resourcesHelper.THEME}/img/documents-icon.png`}
                                 alt="Documents icon"
                                 to={`/dashboard/annualMotorDocuments/${policyNumber}`}
                                 hasInfoButton={true}
                                 tourDriver={this.props.tourDriver}
                                 infoModalTitle={resourcesHelper.DOCUMENTS_TITLE}
                                 infoModalBulletPoints={["View your policy documents", "View your old policy documents",
                                     "Request documents to be sent by post"]}
                                 {...props}>
                        {resourcesHelper.DOCUMENTS_TITLE}
                    </ImgTileLink>
                </div>}

                    {hasPolicies && <div className="flexItem make-a-claim">
                        <ImgTileLink id="claimsTile"
                                     src={`/${resourcesHelper.THEME}/img/make-a-claim.png`}
                                     alt="Make a claim icon"
                                     to={motorClaimsLink}
                                     openInNewWindow={useMotorDirectClaimsLink}
                                     hasInfoButton={true}
                                     tourDriver={this.props.tourDriver}
                                     infoModalTitle="Make a claim"
                                     infoModalBulletPoints={["Make a windscreen or window claim",
                                         "Make a breakdown claim",
                                         "Make other claims (road traffic accident, theft, stolen keys etc.)"]}
                                     {...props}>
                            Make a claim
                        </ImgTileLink>
                    </div>}
                    <div className="flexItem quotes">
                        <ImgTileLink id="getQuoteTile"
                                     src={`/${resourcesHelper.THEME}/img/savings_icon.png`}
                                     alt="Products icon"
                                     to={`/dashboard/getNewQuote`}
                                     routeState={{
                                         policy,
                                     }}
                                     hasInfoButton={true}
                                     tourDriver={this.props.tourDriver}
                                     infoModalTitle={productIconText.modalTitle}
                                     infoModalBulletPoints={productIconText.bullets}
                                     infoModalButton={productIconText.modalButton}
                                     badgeText={quoteBadgeText}
                                     mobileBadgeText={quoteMobileBadgeText}
                                     {...props}>
                            {productIconText.tileLabel({hasPolicies: Boolean(hasPolicies)})}
                        </ImgTileLink>
                    </div>

                    {hasPolicies && !hasMultiplePolicies && <div className="flexItem account-details">
                        <ImgTileLink src={`/${resourcesHelper.THEME}/img/account-details.png`}
                                     alt="Account details icon"
                                     to={`/dashboard/annualMotorAccount/${policyNumber}`}
                                     hasInfoButton={true}
                                     tourDriver={this.props.tourDriver}
                                     infoModalTitle="Account details"
                                     infoModalBulletPoints={["View and amend account holder details (e.g. email, phone number, address, password)",
                                         "Opt in to auto-renew your policy"]}
                                     {...props}>
                            Account details
                        </ImgTileLink>
                    </div>}

                    <div className="flexItem help">
                        <ImgTileLink src={`/${resourcesHelper.THEME}/img/help.png`}
                                     alt="Help icon"
                                     to={isBritannia ? "/dashboard/help" : FAQ_CAR_URL}
                                     openInNewWindow={!isBritannia}
                                     hasInfoButton={true}
                                     tourDriver={this.props.tourDriver}
                                     infoModalTitle="Help"
                                     infoModalBulletPoints={[
                                         "View 'How to' videos and guides",
                                         "Read FAQs about your cover, account, payment, breakdown, renewal, claims, NCD..."]}
                                     {...props}>
                            Help
                        </ImgTileLink>
                    </div>

                    {!inGracePeriod && hasPolicies && !policyCanceled && (<div className="flexItem cancel-policy">
                            {hasPendingRenewal ?
                                (
                                    <ImgTileLink src={`/${resourcesHelper.THEME}/img/cancel-policy-icon.png`}
                                                 alt="Cancel policy"
                                                 to={`/dashboard/annualMotor/cancel/${policyNumber}`}
                                                 routeState={{
                                                     isRenewalCancellation: true,
                                                     renewalCancellationUrl: '/journey/init/product/AnnualMotorInsurance/process/renewal/renewalMTCEntryPage/' + renewalProposal!._id
                                                 }}
                                                 hasInfoButton={true}
                                                 tourDriver={this.props.tourDriver}
                                                 infoModalTitle="Cancel policy"
                                                 infoModalBulletPoints={["Cancel your policy"]}
                                                 {...props}
                                    >
                                        Cancel policy
                                    </ImgTileLink>
                                ) : (
                                    <ImgTileLink src={`/${resourcesHelper.THEME}/img/cancel-policy-icon.png`}
                                                 alt="Cancel policy"
                                                 to={`/journey/init/product/AnnualMotorInsurance/process/mtc/presetPage/${policy!.interaction?._id}`}
                                                 hasInfoButton={true}
                                                 tourDriver={this.props.tourDriver}
                                                 infoModalTitle="Cancel policy"
                                                 infoModalBulletPoints={["Cancel your policy"]}
                                                 {...props}
                                    >
                                        Cancel policy
                                    </ImgTileLink>
                                )
                            }
                        </div>
                    )}

                    {(hasExpiredPolicies || hasOldPolicies) && hasActivePolicy && (<div className="flexItem old-policies">
                            <ImgTileLink src={`/${resourcesHelper.THEME}/img/old-policies.png`}
                                         alt="Old policies"
                                         to={hasOldHomePolicies ? "/dashboard/annualOldPolicies" : "/dashboard/annualMotorOldPolicies"}
                                         hasInfoButton={true}
                                         tourDriver={this.props.tourDriver}
                                         infoModalTitle="Old policies"
                                         infoModalBulletPoints={["View your previous policies",
                                             "Make a claim on your previous policies"]}
                                         {...props}>
                                Old policies
                            </ImgTileLink>
                        </div>
                    )}

                    {resourcesHelper.THEME === "Allianz" && hasActivePolicy && !hasMultiplePolicies && !isAllianzAdvantagesToggleEnabled() &&
                        <div className="flexItem savings">
                            <ImgTileLink src={`/${resourcesHelper.THEME}/img/gift_icon.svg`}
                                         alt="Offers icon"
                                         to={`https://www.allianz.co.uk/insurance/car-insurance/customer-offers`}
                                         openInNewWindow={true}
                                         hasInfoButton={true}
                                         tourDriver={this.props.tourDriver}
                                         infoModalTitle="My offers"
                                         infoModalBulletPointTitle="Our way of saying thanks"
                                         infoModalBulletPoints={["Save with exclusive offers from Allianz"]}
                                         {...props}>
                                My offers
                            </ImgTileLink>
                        </div>}

                    {hasActivePolicy && isAllianzAdvantagesToggleEnabled() && <div className="flexItem advantages">
                        <ImgTileLink id="getAdvantagesTile"
                                     src={`/${resourcesHelper.THEME}/img/allianz-star.svg`}
                                     alt={`${resourcesHelper.THEME} Advantages icon`}
                                     to={`/dashboard/advantages`}
                                     routeState={{
                                         policy,
                                     }}
                                     infoModalBulletPointTitle={`Our way of saying thanks`}
                                     hasInfoButton={true}
                                     tourDriver={this.props.tourDriver}
                                     infoModalTitle="Allianz Advantages"
                                     infoModalBulletPoints={["Save with exclusive offers from Allianz"]}
                                     infoModalButton="Allianz Advantages"
                                     {...props}>
                            {resourcesHelper.THEME} Advantages
                        </ImgTileLink>
                    </div>}

                    {hasPolicies && !hasMultiplePolicies && <div className="flexItem marketing-preferences">
                        <ImgTileLink src={`/${resourcesHelper.THEME}/img/exclamation-mark-dark.svg`}
                                     alt="Marketing preferences"
                                     to={"/dashboard/marketing-preferences"}
                                     hasInfoButton={true}
                                     tourDriver={this.props.tourDriver}
                                     infoModalTitle="Marketing preferences"
                                     infoModalBulletPoints={["Change your marketing preferences (hear about our latest products, services and discounts)"]}
                                     {...props}>
                            Marketing preferences
                        </ImgTileLink>
                    </div>}

                </div>
            </div>
        );
    }
}
